// live/liveView.ts (GH3) - the adaptor from a live engine PostflopSnapshot to the props the reused surface
// sub-components render. It carries NO strategy: the option set is snapshot.ramp (the true legal set),
// the grade is snapshot.grade(user) (the real engine EV grade), and the coach text is snapshot.coach
// (deterministic $0 templates). This file only shapes those into the ReadLockIn / MathAid / BetMathBuilder
// / GradeReveal / NarrationBeats contracts the authored surfaces already use - so the live surfaces reuse
// every component verbatim and the difference from a guided lesson is purely the data source.
import type { ActionKind, ArchetypeId } from '@postflop/schema';
import type { FamilyDef, PriceDef, BetMathDef, PlayerTag } from '../diagnose/data';
import { TAG_ARCHETYPE } from '../diagnose/data';
import type { ObserveBeat, ObserveStreetData } from '../observe/data';
import { MOVE_CLASS_FROM_BAND, MOVE_CLASS_LABELS, type MoveClass, type ResolvedGrade } from '../grade/types';
import { money } from '../engine/bridge';
import { archetypeOf } from '../engine/liveData';
import type { PostflopSnapshot, LiveHandModel } from '../engine/liveRunner';
import { displayName } from '../engine/liveRunner';
import type { UserDecision } from '@postflop/engine';
import type { CoachLine } from '@postflop/coach';

/** The five hand families the read UI weights the villain into (board-generic, reused across live hands -
 *  same set the authored Diagnose hand uses). */
export const LIVE_FAMILIES: FamilyDef[] = [
  { name: 'Nuts', detail: 'sets, two pair' },
  { name: 'Strong made', detail: 'a better made hand' },
  { name: 'Medium pairs', detail: 'second pair, weak top' },
  { name: 'Draws', detail: 'flush / straight outs' },
  { name: 'Air, bluff', detail: 'pure nothing' },
];

const STREET_KICKER: Record<string, string> = {
  flop: 'The flop', turn: 'The turn', river: 'The river', preflop: 'Preflop',
};

/** Plain-English display label for a ramp/committed action. */
export const actionLabel = (action: ActionKind, amount?: string): string => {
  switch (action) {
    case 'fold': return 'Fold';
    case 'check': return 'Check';
    case 'call': return 'Call';
    case 'bet': return amount ? `Bet $${amount}` : 'Bet';
    case 'raise': return amount ? `Raise to $${amount}` : 'Raise';
    case 'jam': return 'All in';
  }
};

/** Map a ramp action + a typed amount into the engine UserDecision the grader consumes. bet/raise carry a
 *  sizing INTENT derived from the typed dollars vs the pot (do-your-own-math stays intact - the user typed
 *  the number; we only bucket it so the EV grader can score the sizing fit). */
export const userDecisionFrom = (
  action: ActionKind,
  amount: string | undefined,
  pot: number,
  toCall: number,
): UserDecision => {
  if (action === 'bet' || action === 'raise') {
    const n = Number(String(amount ?? '').replace(/[^0-9.]/g, ''));
    const invested = action === 'bet' ? n : Math.max(0, n - toCall);
    const ratio = pot > 0 ? invested / pot : 0.66;
    const intent = ratio <= 0.4 ? 'third' : ratio <= 0.6 ? 'half' : ratio <= 0.8 ? 'twoThirds' : ratio <= 1.05 ? 'pot' : 'overbet';
    return { action, sizingIntent: intent };
  }
  if (action === 'jam') return { action: 'jam', sizingIntent: 'jam' };
  return { action };
};

/** The app-shown price for a street: the headline required-equity % (postflop) and the pot-odds trick. */
export const livePrice = (s: PostflopSnapshot): PriceDef => {
  const { pot, toCall } = s.price;
  if (toCall <= 0) {
    return {
      title: 'The plan',
      big: 'Lead',
      full: ['No bet to face, you are first to act. Size to what you want called, not to a fixed fraction.'],
      shorthand: 'First in, no price. Bet what gets called by worse.',
    };
  }
  const potAfter = pot + toCall;
  const reqEq = Math.round((toCall / potAfter) * 100);
  return {
    title: 'The price',
    big: `${reqEq}%`,
    full: [
      `He bet ${money(toCall)} into ${money(pot)}. Call and you risk ${money(toCall)} to play for a ${money(potAfter)} pot, so you need to be ahead about `,
      { term: 'reqeq', text: 'required equity' },
      ` ${reqEq}% of the time.`,
    ],
    shorthand: `${money(toCall)} to win a ${money(potAfter)} pot. ${toCall} / ${potAfter} is about ${reqEq}%.`,
  };
};

/** The manual bet-math config for the street (null when there is no bet/raise option, e.g. a river shove
 *  hero can only call). Quick chips are pot-relative sizes; NOTHING pre-fills the input. */
export const liveBetDef = (s: PostflopSnapshot): BetMathDef | null => {
  const raise = s.ramp.find((r) => r.key === 'raise');
  const bet = s.ramp.find((r) => r.key === 'bet');
  const chip = raise ?? bet;
  if (!chip) return null;
  const { pot, toCall } = s.price;
  const facing = toCall > 0;
  // quick sizes: pot-relative, clamped to the engine's legal min/max.
  const fracs: Array<{ f: number; r: string }> = facing
    ? [{ f: 2.5, r: '2.5x' }, { f: 3, r: '3x' }, { f: 4, r: '4x' }]
    : [{ f: 0.5, r: '½ pot' }, { f: 0.66, r: '⅔ pot' }, { f: 1, r: 'pot' }];
  const quick = fracs.map(({ f, r }) => {
    const raw = facing ? Math.round(toCall * f) : Math.round(pot * f);
    const clamped = Math.max(chip.min ?? raw, Math.min(chip.max ?? raw, raw));
    return { amount: `$${clamped}`, ratio: r };
  });
  return {
    pfx: facing ? 'Raise to' : 'Bet',
    pot: money(pot),
    call: money(toCall),
    callLabel: 'To call',
    vbet: facing ? money(toCall) : 'none',
    sub: facing
      ? 'Build the number yourself. Ask first what worse hand pays it.'
      : 'Build the number yourself. Size to the worse hands you want to call.',
    quick,
  };
};

/** Generated kicker / headline / lede for the street, from the real board + villain + price. */
export const liveHeadline = (s: PostflopSnapshot, _model: LiveHandModel): { kicker: string; h2: string; lede: string; tagSub: string } => {
  const { pot, toCall } = s.price;
  const facing = toCall > 0;
  const kicker = STREET_KICKER[s.street] ?? s.street;
  // TB5: name the villain hero faces AT THIS node (per-node `villainArchetypeId`), the same man Axis B grades
  // against and the tag persists to - not `model.villainName`, the hand-level subject, which is a different
  // player whenever a later street is led by someone other than the flop aggressor.
  const villain = displayName(s.villainArchetypeId);
  const h2 = facing
    ? `${villain} bets ${money(toCall)} into ${money(pot)}`
    : `${villain} checks to you`;
  const lede = facing
    ? `You are ${s.heroEquityPct}% to be ahead here. Read the seat, then decide the price.`
    : `The action is on you for ${money(pot)}. Read the seat, then choose your line.`;
  const tagSub = `The main opponent is ${villain}. What kind of player is he?`;
  return { kicker, h2, lede, tagSub };
};

// ── the two-axis LIVE grade (ResolvedGrade for <GradeReveal>) ─────────────────────────────────────────

/** Grade the read (Axis B): the tagged archetype vs the villain's REAL archetype. Exact → best; sharing
 *  the betting-volume imbalance (an adjacent read) → excellent; otherwise → inaccuracy. Uses real D1 data.
 *  Exported so the preflop grade (PF4) reuses the SAME read-axis logic as the postflop grade. */
export const gradeReadAxis = (tag: PlayerTag | null, villainArchId: string): { cls: MoveClass; right: boolean; label: string; text: string } => {
  const guessed = tag ? TAG_ARCHETYPE[tag] : null;
  if (guessed && guessed === villainArchId) {
    return { cls: 'best', right: true, label: 'Read the player right', text: 'You put him on the right type. That is the read the whole hand hangs on.' };
  }
  if (guessed) {
    const g = archetypeOf(guessed);
    const v = archetypeOf(villainArchId as never);
    if (g.imbalances.bettingVolume === v.imbalances.bettingVolume) {
      return { cls: 'exc', right: false, label: 'Close read', text: `Not exact, but you had his betting tendency right. He plays closer to ${displayName(v.id)}.` };
    }
    return { cls: 'inacc', right: false, label: 'Read a little off', text: `He is really ${displayName(v.id)}. Different tendencies mean a different plan.` };
  }
  return { cls: 'inacc', right: false, label: 'No read locked', text: `Lock a read next time. He was ${displayName(villainArchId as ArchetypeId)}.` };
};

/** Pick the most explanatory coach line for the play (ruleFired > debrief > price > first). */
const primaryCoach = (coach: CoachLine[]): string => {
  const byKind = (k: string) => coach.find((c) => c.kind === k)?.text;
  return byKind('ruleFired') ?? byKind('debrief') ?? byKind('price') ?? coach[0]?.text ?? 'The engine graded this line on its EV.';
};

/** Build the full two-axis reveal for a committed live decision. Axis A is the engine's EV grade of the
 *  play (via MOVE_CLASS_FROM_BAND); Axis B is the read grade; the verdict combines them; the coach callout
 *  and debrief draw on the real template coach lines. */
export const buildLiveGrade = (
  s: PostflopSnapshot,
  tag: PlayerTag | null,
  action: ActionKind,
  amount: string | undefined,
): { resolved: ResolvedGrade; grade: ReturnType<PostflopSnapshot['grade']> } => {
  const user = userDecisionFrom(action, amount, s.price.pot, s.price.toCall);
  const result = s.grade(user);
  const engineGrade = result.grade;
  const aCls = MOVE_CLASS_FROM_BAND[engineGrade.gradeBand];
  const actionGood = aCls === 'best' || aCls === 'great' || aCls === 'exc';

  const read = gradeReadAxis(tag, s.villainArchetypeId);
  const coachText = primaryCoach(result.coach);

  const good = actionGood && read.right;
  const verdictCls: MoveClass = good ? 'best' : actionGood || read.right ? 'inacc' : 'blun';
  const verdictLabel = good
    ? 'Well read, well played'
    : actionGood
      ? 'Right play, read a little off'
      : read.right
        ? 'Right read, the play slipped'
        : 'Read and play both off';
  const mark = good ? '✓' : verdictCls === 'inacc' ? '!' : '✕';

  const bookLine = `${actionLabel(s.bookAction)}${s.recommendation.sizingIntent && (s.bookAction === 'bet' || s.bookAction === 'raise') ? ' @ ' + s.recommendation.sizingIntent : ''}`;
  // did the user take the engine's own recommended (legalized) action? (The EV grade can still ding a
  // rule-based rec - the documented honesty valve - so we must NOT tell the user "the book line is X"
  // when they DID X.)
  const matchedBook = action === s.bookAction;
  const callout: ResolvedGrade['coach'] = actionGood
    ? { lead: 'Carry this forward.', text: coachText, bad: false }
    : matchedBook
      ? { lead: 'A thin spot.', text: `A defensible line. The EV model just sees a slimmer edge here than the read alone suggests. ${coachText}`, bad: false }
      : { lead: 'Where it slipped.', text: `The book line here is ${bookLine}. ${coachText}`, bad: true };

  const resolved: ResolvedGrade = {
    verdict: {
      cls: verdictCls,
      mark,
      label: verdictLabel,
      sub: 'Graded on the read and the play, not on whether you won.',
    },
    axisA: {
      badge: 'A',
      kicker: 'Your play, the hand',
      cls: aCls,
      label: `${actionLabel(action, amount)}`,
      text: `${coachText}${engineGrade.evLossBB && engineGrade.evLossBB > 0.5 ? ` Costs about ${engineGrade.evLossBB.toFixed(1)}bb versus the best line.` : ''}`,
    },
    axisB: {
      badge: 'B',
      kicker: 'Your read, the game',
      cls: read.cls,
      label: read.label,
      text: read.text,
    },
    coach: callout,
    debrief: {
      title: 'The read behind it',
      body: result.coach.map((c) => c.text).join(' '),
      example: { label: 'Book line:', text: bookLine },
    },
  };
  return { resolved, grade: result };
};

// ── Observe: narration beats from the real coach lines + the pro's engine line ────────────────────────

const TONE_TAG: Record<string, string> = { good: 'I bet', caution: 'I slow down', bad: 'I fold', neutral: 'I think' };

/** Build the Observe street model for a live hand: the pro's read as numbered beats sourced from the real
 *  template coach lines, plus a decision beat carrying the engine's recommended line. */
export const liveObserveStreet = (s: PostflopSnapshot, model: LiveHandModel): ObserveStreetData => {
  const { pot, toCall } = s.price;
  const facing = toCall > 0;
  const beats: ObserveBeat[] = [];
  // a framing beat.
  beats.push({
    tokens: [
      facing ? `${model.villainName} bets ${money(toCall)} into ${money(pot)}. ` : `Checked to me for ${money(pot)}. `,
      `I read myself as ${s.heroEquityPct}% here.`,
    ],
  });
  // the template coach lines as read beats.
  for (const c of s.coach.slice(0, 3)) {
    beats.push({ tokens: [c.text] });
  }
  // the decision beat - what the pro does (the engine line, legalized to what he can actually do).
  const recLabel = `${actionLabel(s.bookAction)}${s.recommendation.sizingIntent && (s.bookAction === 'bet' || s.bookAction === 'raise') ? ' ' + s.recommendation.sizingIntent : ''}`;
  beats.push({
    decision: true,
    tag: TONE_TAG[s.bookAction === 'fold' ? 'bad' : s.bookAction === 'check' || s.bookAction === 'call' ? 'neutral' : 'good'] ?? 'I act',
    tokens: [`So I ${recLabel.toLowerCase()}.`],
  });

  const level = (Math.min(3, Math.max(1, s.streetIndex)) as 1 | 2 | 3);
  return {
    id: s.street as ObserveStreetData['id'],
    kicker: STREET_KICKER[s.street] ?? s.street,
    h2: facing ? `${model.villainName} bets ${money(toCall)}` : `${model.villainName} checks`,
    lede: `Watch the read build. ${model.villainName} is the one to track.`,
    level,
    bucketTitle: 'What he can have here',
    bucketSub: 'The pro keeps a running weight on each family as the hand plays.',
    families: null, // live weights are a later refinement; the pro narrates them in the beats.
    beats,
  };
};

/** Grade the move class for a Solo/live street, exposed so the report can color it. */
export const moveClassLabel = (cls: MoveClass): string => MOVE_CLASS_LABELS[cls];

// ── the LIVE end-of-hand report - reuses the shared report helpers but labels rows by the REAL street
//    (a live hand may be flop-first, so buildDiagnoseReport's index-based Preflop/Flop/Turn labels are wrong).
import { ACC, badgeFor, bandCounts, overallAccuracy, type ReportModel, type ReportStreet } from '../report/report';

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** One accumulated live decision for the report: the two-axis grade + the real street it was on. */
export interface LiveReportEntry {
  grade: ResolvedGrade;
  street: string;
}

/** Build the end-of-hand report from the REAL live grades, labeled by their real streets. Reuses the
 *  shared ACC / bandCounts / overallAccuracy / badgeFor so the accuracy line, band chips, and rows render
 *  through the same <EndOfHandReport> as the authored surfaces. Verdict copy is derived from the real mix. */
export const buildLiveReport = (entries: LiveReportEntry[], voice: 'diagnose' | 'solo'): ReportModel => {
  const streets: ReportStreet[] = entries.map((e) => {
    const cls = e.grade.verdict.cls;
    let action = e.grade.axisA.label;
    if ((e.grade.axisA.cls === 'best' || e.grade.axisA.cls === 'exc') && e.grade.axisB.cls !== 'exc' && e.grade.axisB.cls !== 'best') {
      action += ' . read off';
    }
    return { name: cap(e.street), acc: ACC[cls], cls, action };
  });
  const accuracy = overallAccuracy(streets);
  const bands = bandCounts(streets);
  const bestCount = entries.filter((e) => e.grade.verdict.cls === 'best').length;
  const anyPlayWrong = entries.some((e) => !(e.grade.axisA.cls === 'best' || e.grade.axisA.cls === 'exc'));

  let verdict: string;
  let sub: string;
  if (entries.length === 0) {
    verdict = 'Hand ended early';
    sub = 'No decisions were graded this hand.';
  } else if (bestCount === entries.length) {
    verdict = 'Textbook, start to finish';
    sub = 'You read the player right and took the best line every street.';
  } else if (!anyPlayWrong) {
    verdict = 'Strong line, read slipped';
    sub = 'Every play was sound; a read or two came in a touch fast.';
  } else if (accuracy >= 75) {
    verdict = 'Solid, one misplay';
    sub = 'The read mostly held, but one street cost you. Replay it and read first.';
  } else if (accuracy >= 55) {
    verdict = 'The line slipped';
    sub = 'A decision or two got away. Read the player, then act.';
  } else {
    verdict = 'Rough hand';
    sub = 'Several decisions missed. Trust the read before the cards.';
  }

  return {
    kicker: 'End of hand',
    h2: voice === 'solo' ? 'How you flew it' : 'How you played the hand',
    lede: 'Across the streets you played. Graded on the read and the play, not on whether you won.',
    accuracy,
    badgeCls: badgeFor(accuracy),
    verdict,
    sub,
    streets,
    bands,
  };
};
